/**
 * pushNotifications.ts
 * Serviço cliente para Web Push Notifications.
 * Gerencia subscribe / unsubscribe e persiste a subscription no Supabase.
 */

import { supabase } from '@/lib/integrations/supabase'

// VAPID public key (base64url) — configure em VITE_VAPID_PUBLIC_KEY
const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined

// ─── Helpers de conversão ────────────────────────────────────

/**
 * Converte uma chave VAPID em base64url para Uint8Array,
 * que é o formato esperado pelo pushManager.subscribe().
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  const output = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; i++) {
    output[i] = rawData.charCodeAt(i)
  }
  return output
}

function arrayBufferToBase64(buffer: ArrayBuffer | null): string {
  if (!buffer) return ''
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
}

// ─── Tipos ───────────────────────────────────────────────────

export interface PushSubscriptionData {
  endpoint: string
  keys: { p256dh: string; auth: string }
}

// ─── API pública ─────────────────────────────────────────────

/**
 * Retorna true se o navegador suporta Web Push.
 */
export function isPushSupported(): boolean {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

/**
 * Retorna o status de permissão atual: 'default' | 'granted' | 'denied'
 */
export function getNotificationPermission(): NotificationPermission {
  return Notification.permission
}

/**
 * Registra o Service Worker e cria uma subscription Web Push.
 * Salva a subscription no Supabase (tabela push_subscriptions).
 * Lança erro se qualquer etapa falhar.
 */
export async function subscribeToPush(userId: string): Promise<PushSubscriptionData> {
  if (!isPushSupported()) {
    throw new Error('Web Push não suportado neste navegador.')
  }
  if (!VAPID_PUBLIC_KEY) {
    throw new Error('VITE_VAPID_PUBLIC_KEY não configurado.')
  }

  // Pedir permissão
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') {
    throw new Error('Permissão para notificações negada pelo usuário.')
  }

  // Registrar (ou obter) o Service Worker
  const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' })
  await navigator.serviceWorker.ready

  // Criar subscription
  const pushSubscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as unknown as ArrayBuffer,
  })

  const subscriptionData: PushSubscriptionData = {
    endpoint: pushSubscription.endpoint,
    keys: {
      p256dh: arrayBufferToBase64(pushSubscription.getKey('p256dh')),
      auth: arrayBufferToBase64(pushSubscription.getKey('auth')),
    },
  }

  // Obter timezone do dispositivo
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

  // Persistir no Supabase (upsert para evitar duplicatas)
  const { error } = await supabase.from('push_subscriptions').upsert(
    {
      user_id: userId,
      endpoint: subscriptionData.endpoint,
      keys: subscriptionData.keys,
      user_agent: navigator.userAgent,
      timezone,
    },
    { onConflict: 'user_id,endpoint' }
  )
  if (error) throw new Error(`Erro ao salvar subscription: ${error.message}`)

  return subscriptionData
}

/**
 * Cancela a subscription ativa e remove do Supabase.
 */
export async function unsubscribeFromPush(userId: string): Promise<void> {
  if (!isPushSupported()) return

  const registration = await navigator.serviceWorker.getRegistration('/sw.js')
  if (!registration) return

  const pushSubscription = await registration.pushManager.getSubscription()
  if (!pushSubscription) return

  const endpoint = pushSubscription.endpoint

  await pushSubscription.unsubscribe()

  const { error } = await supabase
    .from('push_subscriptions')
    .delete()
    .eq('user_id', userId)
    .eq('endpoint', endpoint)

  if (error) throw new Error(`Erro ao remover subscription: ${error.message}`)
}

/**
 * Verifica se já existe uma subscription ativa para o usuário neste dispositivo.
 */
export async function getCurrentSubscription(): Promise<PushSubscription | null> {
  if (!isPushSupported()) return null
  const registration = await navigator.serviceWorker.getRegistration('/sw.js')
  if (!registration) return null
  return registration.pushManager.getSubscription()
}
